/**
 * Where the browser finds `app_api`.
 *
 * The platform has **one** backend on its own hostname — `api.kkfmongolia.com` — and five
 * front ends on five others. That was an open question until 2026-09-19 and it is answered:
 * a dedicated origin with CORS, not an edge proxy. The API already answers on that hostname
 * (`docs/04-operations/api-deploy.md`), so the choice cost no DNS record; what it costs is a
 * cross-origin request, which `CORS_ALLOW_ORIGIN_REGEX` on the API side already allows. The
 * alternative — a Pages Function forwarding `/api/*` from each of five projects — is five
 * proxies to keep in step with one API, and the day one of them drifts the symptom is a 404
 * on one site only.
 *
 * This module is the *one* place that decides the root. Before it there were three
 * (`AuthProvider`, `app_steward/src/api/client.ts` and `app_partners/src/api/index.ts`),
 * each with the same literal, which is exactly what ground rule 5 exists to stop: moving the
 * API would have been a three-file change with no gate that notices the file you missed.
 *
 * ## Why it takes an env record
 *
 * The same reason `resolveSiteLinks` does: `import.meta` is a bundler-specific global and
 * `app_design` has to stay importable from React Native when `app_mobile` arrives. The
 * caller passes its own environment.
 *
 *     const API_ROOT = resolveApiRoot(import.meta.env)   // Vite apps
 *
 * ## Why the default is not simply the production host
 *
 * Every other URL on this platform defaults to production, so that a build with no `.env`
 * at all is a correct production build. This one does that **only in a production build**,
 * and falls back to the same-origin `/api/v1` path in development.
 *
 * The asymmetry is deliberate and it is about blast radius. A link that defaults to
 * production sends a developer to a live page; an API root that defaults to production
 * posts a join application, signs somebody in, or writes a row into the live database from
 * a laptop. `app_api/.env.secrets` in this repository already points at `kkf_prod`. So in
 * development an unset variable means "the Vite proxy on `/api`", which is the dev
 * scaffolding each app's `vite.config.ts` already configures and which `KKF_API_URL` points
 * at a local API. Setting `VITE_API_URL` in `.env.local` overrides that when you do want to
 * work against a deployed API — it wins in both modes.
 */

/** The variable that configures it. Public, like every other `VITE_` value. */
export const API_ORIGIN_ENV_VAR = 'VITE_API_URL'

/** The one backend, on its own hostname. */
export const PRODUCTION_API_ORIGIN = 'https://api.kkfmongolia.com'

/**
 * The development fallback: the path each app's Vite dev proxy answers.
 *
 * `/api` rather than `/v1` because the proxy strips the prefix — the API mounts its
 * versioned sub-app at `/v1`, so a dev request for `/api/v1/auth/login` arrives as
 * `/v1/auth/login`. See the `proxy` block in any app's `vite.config.ts`.
 */
export const DEV_API_ROOT = '/api/v1'

/** An env value only counts if it is a non-empty string — an unset Vite var is `''`. */
const configured = (value: unknown): value is string => typeof value === 'string' && value.trim() !== ''

/**
 * An absolute origin, with no trailing slash.
 *
 * **A missing scheme is repaired rather than passed through**, and that is the one piece of
 * defensiveness in this file. `VITE_API_URL=api.kkfmongolia.com` is the obvious thing to type
 * into a Cloudflare dashboard field, and it does not fail: it makes every request *relative*,
 * so `fetch` resolves it against the page — `https://partners.kkfmongolia.com/api.kkfmongolia.com/v1/auth/login`.
 * Pages answers that with `index.html` under the SPA fallback, 200, and the first thing that
 * goes wrong is `JSON.parse` on a page of HTML, on one deployment, in a browser nobody is
 * watching. There is no gate anywhere that sees it.
 *
 * `//host` is left alone: a protocol-relative URL is a deliberate thing to write and already
 * absolute. Anything already carrying a scheme is untouched.
 */
const origin = (value: string): string => {
  const trimmed = value.trim().replace(/\/+$/, '')
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) || trimmed.startsWith('//')) return trimmed
  return `https://${trimmed}`
}

/**
 * The root every request is built on — an origin plus the API's version mount, with no
 * trailing slash, so `${root}/auth/login` is always one slash.
 *
 * @param env any string map — `import.meta.env`, `process.env`, or a literal in a test.
 *            `DEV` is read from it the way Vite sets it; a record without one is treated
 *            as a production build, which is the safe direction for a caller that has no
 *            such concept.
 */
export function resolveApiRoot(env: Readonly<Record<string, unknown>> = {}): string {
  const configuredOrigin = env[API_ORIGIN_ENV_VAR]
  if (configured(configuredOrigin)) return `${origin(configuredOrigin)}/v1`
  return env.DEV === true ? DEV_API_ROOT : `${PRODUCTION_API_ORIGIN}/v1`
}
